import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, Plus, Calendar, RefreshCw, Pause, Play, X,
  Sparkles, Leaf, Waves, Wind, Wrench, Clock, DollarSign, MapPin, ChevronRight
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  cat_diarista: Sparkles,
  cat_jardinagem: Leaf,
  cat_piscina: Waves,
  cat_arcondicionado: Wind,
  cat_reforma: Wrench
};

const FREQUENCY_LABELS = {
  weekly: "Semanal",
  biweekly: "Quinzenal",
  monthly: "Mensal"
};

const DAY_NAMES = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

export default function SubscriptionsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptions();
  }, [token]);

  const fetchSubscriptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setSubscriptions(await response.json());
      }
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      toast.error("Erro ao carregar assinaturas");
    } finally {
      setLoading(false);
    }
  };

  const pauseSubscription = async (subscriptionId) => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/${subscriptionId}/pause`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Assinatura pausada");
        fetchSubscriptions();
      }
    } catch (error) {
      toast.error("Erro ao pausar assinatura");
    }
  };

  const resumeSubscription = async (subscriptionId) => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/${subscriptionId}/resume`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Assinatura retomada");
        fetchSubscriptions();
      }
    } catch (error) {
      toast.error("Erro ao retomar assinatura");
    }
  };

  const cancelSubscription = async (subscriptionId) => {
    if (!confirm("Tem certeza que deseja cancelar esta assinatura?")) return;

    try {
      const response = await fetch(`${API_URL}/api/subscriptions/${subscriptionId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Assinatura cancelada");
        fetchSubscriptions();
      }
    } catch (error) {
      toast.error("Erro ao cancelar assinatura");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="subscriptions-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate(user?.role === "client" ? "/client" : "/provider")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-slate-600" />
                <span className="font-semibold text-slate-900">Assinaturas</span>
              </div>
            </div>
            {user?.role === "client" && (
              <Button onClick={() => navigate("/subscriptions/new")} data-testid="new-subscription-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nova Assinatura
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {user?.role === "client" ? "Minhas Assinaturas" : "Assinaturas Recebidas"}
          </h1>
          <p className="text-slate-600">
            Serviços recorrentes agendados automaticamente
          </p>
        </div>

        {subscriptions.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <RefreshCw className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Nenhuma assinatura
              </h3>
              <p className="text-slate-500 mb-4">
                {user?.role === "client" 
                  ? "Crie uma assinatura para serviços recorrentes como limpeza, jardinagem ou piscina."
                  : "Você não possui assinaturas de clientes."
                }
              </p>
              {user?.role === "client" && (
                <Button onClick={() => navigate("/subscriptions/new")}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Assinatura
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subscriptions.map((subscription) => {
              const Icon = CATEGORY_ICONS[subscription.category_id] || Sparkles;
              const isActive = subscription.status === "active";
              const isPaused = subscription.status === "paused";

              return (
                <Card key={subscription.subscription_id} className="overflow-hidden">
                  <CardContent className="p-0">
                    {/* Status Header */}
                    <div className={`p-3 ${
                      isActive ? "bg-emerald-50" : isPaused ? "bg-amber-50" : "bg-slate-50"
                    }`}>
                      <div className="flex items-center justify-between">
                        <Badge className={
                          isActive ? "bg-emerald-100 text-emerald-700" :
                          isPaused ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-600"
                        }>
                          {isActive ? "Ativa" : isPaused ? "Pausada" : "Cancelada"}
                        </Badge>
                        <span className="text-sm font-medium text-slate-600">
                          {FREQUENCY_LABELS[subscription.frequency]}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-6 h-6 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">
                            {subscription.category_id?.replace("cat_", "").charAt(0).toUpperCase() + 
                             subscription.category_id?.replace("cat_", "").slice(1)}
                          </p>
                          <p className="text-sm text-slate-500">
                            {subscription.total_services} serviços realizados
                          </p>
                        </div>
                      </div>

                      {/* Schedule */}
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-600">
                            {subscription.frequency === "monthly" 
                              ? `Dia ${subscription.day_of_month} de cada mês`
                              : `Toda ${DAY_NAMES[subscription.day_of_week || 0]}`
                            }
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm mt-2">
                          <Clock className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-600">
                            Próximo: {new Date(subscription.next_service_date).toLocaleDateString("pt-BR")}
                          </span>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">Valor base</span>
                        <span className="text-lg font-bold text-slate-900">
                          R$ {subscription.base_price?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                        </span>
                      </div>

                      {/* Address */}
                      {subscription.address?.city && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <MapPin className="w-4 h-4" />
                          <span>{subscription.address.city}, {subscription.address.state}</span>
                        </div>
                      )}

                      {/* Actions */}
                      {user?.role === "client" && subscription.status !== "cancelled" && (
                        <div className="flex gap-2 pt-2 border-t border-slate-100">
                          {isActive ? (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-1"
                              onClick={() => pauseSubscription(subscription.subscription_id)}
                            >
                              <Pause className="w-4 h-4 mr-1" />
                              Pausar
                            </Button>
                          ) : isPaused && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-1"
                              onClick={() => resumeSubscription(subscription.subscription_id)}
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Retomar
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => cancelSubscription(subscription.subscription_id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Info Card */}
        <Card className="mt-8 bg-slate-900 text-white">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <RefreshCw className="w-10 h-10 text-emerald-400 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-lg mb-2">Como funcionam as assinaturas?</h3>
                <ul className="text-slate-300 text-sm space-y-2">
                  <li>• Serviços agendados automaticamente na frequência escolhida</li>
                  <li>• Mesmo prestador para todos os serviços</li>
                  <li>• Preço fixo acordado</li>
                  <li>• Pause ou cancele a qualquer momento</li>
                  <li>• Ideal para limpeza, jardinagem e piscina</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
